import { EditOutlined } from '@ant-design/icons';
import { ProductSupplierTransactionCheckItem } from '@gooduncles/gu-app-schema';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button, Input, Modal, message } from 'antd';
import { FC, useMemo, useState } from 'react';

import { formatNumber } from 'src/utils/common';
import { updateProductSupplierInventoryReceipt } from 'src/utils/firebase/firebase-short-cut';

import useSWRProductSupplierReceiptRows, {
  ProductSupplierReceiptRow,
} from 'src/hooks/swr/useSWRProductSupplierReceiptRows';

import CardItem from 'src/atomic-components/atoms/CardItem/CardItem';
import Value from 'src/atomic-components/atoms/TextItem/Value';

const defaultColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

const numberFormatter = (params: ValueFormatterParams<ProductSupplierReceiptRow>) =>
  params.value == null || params.value === '' ? '' : formatNumber(params.value);

const columnDefs: ColDef<ProductSupplierReceiptRow>[] = [
  {
    headerName: 'Id',
    field: 'id',
    width: 80,
    hide: true,
  },
  {
    headerName: '재고명',
    field: 'inventoryName',
    width: 300,
  },
  {
    headerName: '단위',
    field: 'inventoryUnit',
    width: 100,
  },
  {
    headerName: '수량',
    field: 'requestValue',
    width: 100,
  },
  {
    headerName: '매입단가',
    field: 'cost',
    width: 100,
    valueFormatter: numberFormatter,
  },
  {
    headerName: '매입가',
    field: 'totalPrice',
    width: 140,
    valueFormatter: numberFormatter,
  },
];

interface ProductSupplierReceiptTransactionTableProps {
  receiptId: string;
  transactionIds: string[];
  checkList: ProductSupplierTransactionCheckItem[];
  onCheckListUpdated: () => void;
}

const ProductSupplierReceiptTransactionTable: FC<ProductSupplierReceiptTransactionTableProps> = ({
  receiptId,
  transactionIds,
  checkList,
  onCheckListUpdated,
}) => {
  const { rows } = useSWRProductSupplierReceiptRows(transactionIds);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const totalPriceSum = useMemo(() => rows.reduce((sum, row) => sum + row.totalPrice, 0), [rows]);

  const pinnedBottomRowData = useMemo(() => [{ inventoryName: '합계', totalPrice: totalPriceSum }], [totalPriceSum]);

  const handleOpen = () => {
    setDraft(checkList.map((item) => item.content).join('\n'));
    setEditing(true);
  };

  const handleSave = async () => {
    // 한 줄이 특이사항 한 건. completed는 콘솔 어디서도 쓰지 않으므로 false로 둔다.
    const nextCheckList: ProductSupplierTransactionCheckItem[] = draft
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((content) => ({ content, completed: false }));

    setSaving(true);
    try {
      await updateProductSupplierInventoryReceipt(
        receiptId,
        { checkList: nextCheckList },
        { audit: { summary: '거래처 입고 특이사항 수정', captureBeforeSnapshot: true } },
      );
      message.success('특이사항을 수정했습니다.');
      setEditing(false);
      onCheckListUpdated();
    } catch (error) {
      message.error('특이사항 수정에 실패했습니다.');
    }
    setSaving(false);
  };

  return (
    <>
      <div className='ag-theme-alpine height100'>
        <AgGridReact
          rowData={rows}
          getRowId={(params) => params.data.id}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          groupDefaultExpanded={-1}
          suppressAggFuncInHeader={true}
          pinnedBottomRowData={pinnedBottomRowData}
        />
      </div>
      <div style={{ padding: 16, backgroundColor: 'white', border: '1px solid var(--gray400)' }}>
        <div className='flexRow flexSpaceBetween' style={{ alignItems: 'center' }}>
          <Value style={{ justifyContent: 'flex-start', fontSize: 14, fontWeight: 600 }}>특이사항</Value>
          <Button size='small' icon={<EditOutlined />} onClick={handleOpen}>
            수정
          </Button>
        </div>
        {checkList.map((item, idx) => (
          <CardItem key={'check-' + idx}>
            <Value style={{ justifyContent: 'flex-start', fontSize: 14, lineHeight: '21px' }}>• {item.content}</Value>
          </CardItem>
        ))}
      </div>
      <Modal
        title='특이사항 수정'
        open={editing}
        onOk={handleSave}
        onCancel={() => setEditing(false)}
        confirmLoading={saving}
        okText='저장'
        cancelText='취소'
      >
        <Input.TextArea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={6}
          placeholder='한 줄에 하나씩 입력합니다.'
        />
      </Modal>
    </>
  );
};

export default ProductSupplierReceiptTransactionTable;
